import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Card } from "@/components/ui/card";

const metadataSchema = z.object({
  title: z.string().min(1, "Track title is required"),
  artist: z.string().min(1, "Artist name is required"),
  album: z.string().min(1, "Album name is required"),
  isrc: z.string().regex(/^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/, "Invalid ISRC code"),
  iswc: z.string().optional(),
  ipi: z.string().optional(),
  genre: z.string().optional(),
  releaseDate: z.string().optional(),
  contributors: z.string().optional(),
});

type MetadataFormData = z.infer<typeof metadataSchema>;

interface MetadataFormProps {
  onSubmit: (data: MetadataFormData) => void;
}

export function MetadataForm({ onSubmit }: MetadataFormProps) {
  const form = useForm<MetadataFormData>({
    resolver: zodResolver(metadataSchema),
    defaultValues: {
      title: "",
      artist: "",
      album: "",
      isrc: "",
      iswc: "",
      ipi: "",
      genre: "",
      releaseDate: "",
      contributors: "",
    },
  });

  return (
    <Card className="w-full max-w-2xl p-6">
      <h2 className="text-2xl font-bold mb-6">Add Metadata</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <Label>Track Title</Label>
                  <FormControl>
                    <Input placeholder="Song name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="artist"
              render={({ field }) => (
                <FormItem>
                  <Label>Artist</Label>
                  <FormControl>
                    <Input placeholder="Artist name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="album"
              render={({ field }) => (
                <FormItem>
                  <Label>Album</Label>
                  <FormControl>
                    <Input placeholder="Album name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="genre"
              render={({ field }) => (
                <FormItem>
                  <Label>Genre</Label>
                  <FormControl>
                    <Input placeholder="e.g., Electronic, Hip-Hop" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="isrc"
              render={({ field }) => (
                <FormItem>
                  <Label>ISRC Code</Label>
                  <FormControl>
                    <Input
                      placeholder="e.g., USRC17607839"
                      className="font-mono"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="releaseDate"
              render={({ field }) => (
                <FormItem>
                  <Label>Release Date</Label>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="iswc"
              render={({ field }) => (
                <FormItem>
                  <Label>ISWC (Optional)</Label>
                  <FormControl>
                    <Input placeholder="International Standard Musical Work Code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ipi"
              render={({ field }) => (
                <FormItem>
                  <Label>IPI (Optional)</Label>
                  <FormControl>
                    <Input placeholder="Interested Parties Information" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="contributors"
            render={({ field }) => (
              <FormItem>
                <Label>Contributors (Optional)</Label>
                <FormControl>
                  <Textarea
                    placeholder="Producers, songwriters, featured artists..."
                    className="resize-none"
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full">
            Generate Metadata
          </Button>
        </form>
      </Form>
    </Card>
  );
}
